import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Container,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

import { useGetBriefQuery, useSendBriefMutation } from "../../../Store/api/rtk";
import { showGlobalToast } from "../../../Components/ToastProvider";
import Question from "./Question";

export default function Brief() {
  const { code } = useParams();
  const navigate = useNavigate();

  const {
    data: briefData,
    isLoading: isFetching,
    isError,
  } = useGetBriefQuery(code);
  const [sendBrief, { isLoading: isSubmitting }] = useSendBriefMutation();

  const questionsList = briefData?.data?.questions || briefData || [];

  // Build initial values, validation schema, and required fields map dynamically
  const { initialValues, validationSchema, isRequiredMap, placeholdersMap } =
    useMemo(() => {
      let initVals = {};
      let schemaFields = {};
      let requiredMap = {};
      let placeholdersMap = {};

      if (questionsList?.length > 0) {
        questionsList.map((q, index) => {
          const fieldName = `${index}`;

          // Store placeholder if exists
          if (q.placeholder) {
            placeholdersMap[fieldName] = q.placeholder;
          }

          // Store is_required flag
          const isRequired = q.is_required !== undefined ? q.is_required : true;
          requiredMap[fieldName] = isRequired;

          if (q.answer_type === "multiple_choice") {
            initVals[fieldName] = [];
            // Add validation only if the field is required
            if (isRequired) {
              schemaFields[fieldName] = yup
                .array()
                .min(1, "انتخاب حداقل یک گزینه الزامی است")
                .required("این فیلد الزامی است");
            } else {
              schemaFields[fieldName] = yup.array().nullable();
            }
          } else {
            initVals[fieldName] = "";
            // Add validation only if the field is required
            if (isRequired) {
              schemaFields[fieldName] = yup
                .string()
                .required("پاسخ به این سوال الزامی است");
            } else {
              schemaFields[fieldName] = yup.string();
            }
          }
        });
      }

      return {
        initialValues: initVals,
        validationSchema: yup.object().shape(schemaFields),
        isRequiredMap: requiredMap,
        placeholdersMap: placeholdersMap,
      };
    }, [questionsList]);

  const formik = useFormik({
    initialValues,
    validationSchema, // Connect schema to formik
    enableReinitialize: true,
    // Attach custom properties for required and placeholder access
    isRequired: isRequiredMap,
    placeholders: placeholdersMap,
    onSubmit: async (values) => {
      try {
        const formattedQuestions = questionsList.map((q, index) => {
          let answerValue = values[`${index}`];

          if (Array.isArray(answerValue)) {
            answerValue = answerValue.join(" ، ");
          }

          return {
            question: q.question,
            answer: String(answerValue || ""),
          };
        });

        const payload = {
          code: code,
          questions: formattedQuestions,
        };

        const res = await sendBrief(payload).unwrap();

        if (res?.status === "ok" || res?.data?.status === "ok") {
          showGlobalToast("اطلاعات بریف با موفقیت ثبت شد", "success");
          navigate("/");
        }
      } catch (error) {
        console.error(error);
        showGlobalToast(
          error?.data?.message || "خطایی در ثبت بریف رخ داد",
          "error",
        );
      }
    },
  });

  const qItems = questionsList.map((q, index) => {
    return <Question formik={formik} q={q} key={index} index={index} />;
  });

  if (isFetching) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress size={50} sx={{ color: "#00ffcc" }} thickness={4} />
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontWeight: 500,
              letterSpacing: "1px",
            }}
          >
            در حال دریافت اطلاعات بریف...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isError || !questionsList?.length) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255, 23, 68, 0.05)",
            border: "1px solid rgba(255, 23, 68, 0.2)",
            borderRadius: "24px",
            p: 5,
            textAlign: "center",
            maxWidth: 400,
            backdropFilter: "blur(12px)",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "#ff1744", mb: 2 }} />
          <Typography
            sx={{ fontSize: 20, fontWeight: 800, color: "#fff", mb: 1 }}
          >
            خطا در دریافت اطلاعات
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 4 }}>
            فرم بریف مورد نظر یافت نشد یا مشکلی در ارتباط با سرور وجود دارد.
          </Typography>
          <Box
            component="button"
            onClick={() => navigate("/")}
            sx={{
              bgcolor: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "12px",
              py: 1.2,
              px: 4,
              fontSize: 15,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            بازگشت به صفحه اصلی
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 10, md: 15 }, position: "relative" }}>
      <Container maxWidth="xxl">
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: "24px",
            bgcolor: "rgba(10, 10, 10, 0.54)",
            // backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderTop: "1px solid rgba(0, 255, 204, 0.4)",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
            position: "relative",
            overflow: "hidden",
            "& *": { direction: "rtl" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "60%",
              height: "200px",
              background:
                "radial-gradient(ellipse, rgba(0,255,204,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <AssignmentOutlinedIcon sx={{ color: "#00ffcc", fontSize: 32 }} />
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 900,
                color: "#fff",
              }}
            >
              تکمیل فرم بریف
            </Typography>
          </Box>
          <Typography sx={{ opacity: 0.6, fontSize: 14, color: "#fff", mb: 5 }}>
            لطفاً اطلاعات زیر را با دقت تکمیل کنید تا بهترین مسیر را برای پروژه
            شما ترسیم کنیم.
          </Typography>

          <Grid container>{qItems}</Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Box
              component="button"
              type="submit"
              disabled={isSubmitting}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "#fff",
                color: "#000",
                border: "none",
                borderRadius: "12px",
                py: 1.8,
                px: 4,
                fontSize: 16,
                fontWeight: 800,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.3s ease",
                boxShadow: "0 4px 14px 0 rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: isSubmitting ? "#fff" : "#00ffcc",
                  transform: isSubmitting ? "none" : "translateY(-2px)",
                  boxShadow: isSubmitting
                    ? "none"
                    : "0 6px 20px rgba(0, 255, 204, 0.25)",
                  "& .arrow-icon": {
                    transform: isSubmitting ? "none" : "translateX(-6px)",
                  },
                },
              }}
            >
              <Typography sx={{ fontWeight: 800, fontFamily: "inherit" }}>
                {isSubmitting ? "در حال ثبت..." : "ثبت اطلاعات بریف"}
              </Typography>
              {!isSubmitting && (
                <Box
                  className="arrow-icon"
                  sx={{ display: "flex", transition: "transform 0.3s ease" }}
                >
                  <ArrowBackIcon fontSize="small" />
                </Box>
              )}
              {isSubmitting && (
                <CircularProgress size={18} sx={{ color: "#000" }} />
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
